import { Combobox } from '@base-ui/react/combobox';
import * as React from 'react';

import type { IconName } from '../../../assets/Icon/Icon';
import type { ComboBoxRow } from './collection';
import { Icon } from '../../../assets/Icon/Icon';
import { baseUIPopupMotionStyles } from '../../../common/animations';
import { devices } from '../../../common/devices';
import { shadows } from '../../../common/shadows';
import { transitions } from '../../../common/transitions';
import { VisuallyHidden } from '../../../common/visually_hidden';
import { zIndexes } from '../../../common/z_indexes';
import { arrowsColor } from '../../../controls/Button/Button.styles';
import { iconColor } from '../../../controls/shared/styles';
import { controlArrowsSize, controlIconSize } from '../../../controls/shared/vars';
import { colors, darkThemeSelector, styled } from '../../../stitches.config';
import { Body } from '../../../text/Body';
import { SmallSansSizes } from '../../../text/Small';
import { fadeMaskCSS, fadeMaskSizeCSS } from '../../../utilities/shared/FadeMask';
import { selectTriggerCSS } from '../../Select/SelectTrigger';

/**
 * The chrome every combobox in the library shares — the select-style trigger, then the portal,
 * positioner, popup, scrolling list, search row and option row. Only the value display and the
 * chips differ per component, and those stay with them.
 */

/**
 * Wears `Select`'s trigger styling, so `ControlGroup` has to know about it to join a combobox to a
 * neighbouring control.
 */
export const ComboBoxTrigger = styled(Combobox.Trigger, selectTriggerCSS);
ComboBoxTrigger.displayName = 'ComboBoxTrigger';

export const ComboBoxTriggerIcon = styled(Icon, {
  size: controlIconSize,
  color: iconColor,
});
ComboBoxTriggerIcon.displayName = 'ComboBoxTriggerIcon';

export const ComboBoxTriggerArrows = styled(Icon, {
  size: controlArrowsSize,
  color: arrowsColor,
});
ComboBoxTriggerArrows.displayName = 'ComboBoxTriggerArrows';

export const ComboBoxPortal = Combobox.Portal;

export interface ComboBoxLabelOptions {
  'aria-label'?: string;
  'aria-labelledby'?: string;
  label?: React.ReactNode;
  placeholder?: string;
}

/**
 * `role="combobox"` takes no accessible name from its contents — those are its value — so the
 * trigger and the dialog popup each need one stated. react-aria got this from the label
 * `HiddenSelect` rendered; Base UI has no equivalent.
 */
export function useComboBoxLabel({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  label,
  placeholder,
}: ComboBoxLabelOptions) {
  const labelId = React.useId();
  const needsLabelNode =
    ariaLabel == null && ariaLabelledby == null && label != null && typeof label !== 'string';
  const labelledBy = ariaLabelledby ?? (needsLabelNode ? labelId : undefined);

  return {
    labelProps: {
      'aria-label': labelledBy
        ? undefined
        : ariaLabel ?? (typeof label === 'string' ? label : undefined) ?? placeholder,
      'aria-labelledby': labelledBy,
    },
    labelNode: needsLabelNode ? <VisuallyHidden id={labelId}>{label}</VisuallyHidden> : null,
  };
}

/** Carried over from the react-aria implementation, which capped its rendered collection here. */
export const COMBOBOX_MAX_ITEMS = 2000;

export const ComboBoxPositioner = styled(Combobox.Positioner, {
  outline: 'none',
  zIndex: zIndexes.overlay,
});
ComboBoxPositioner.displayName = 'ComboBoxPositioner';

export const ComboBoxPopup = styled(Combobox.Popup, baseUIPopupMotionStyles, {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  minWidth: 'var(--anchor-width)',
  maxHeight: 'var(--available-height)',
  overflow: 'hidden',
  backgroundColor: colors.bgApplicationLight,
  borderRadius: '$10',
  boxShadow: shadows.overlayLight,
  outline: 'none',

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },
});
ComboBoxPopup.displayName = 'ComboBoxPopup';

/**
 * Doubles as the list's block padding and the fade's size, as `SectionContent` does with its gutter:
 * a mask is anchored to the scrollport rather than the content, so at either end of the scroll it
 * covers only that padding, and options dim just while they're genuinely passing out of view.
 * `fadeMaskCSS` oversizes by 1.5×, and an option's own 4px of padding absorbs the difference.
 */
export const COMBOBOX_LIST_GUTTER = '$6';

/**
 * The scroll container, rather than the popup, so the search row stays pinned above the options
 * instead of scrolling away with them.
 */
export const ComboBoxList = styled(Combobox.List, fadeMaskCSS(COMBOBOX_LIST_GUTTER, 'vertical'), {
  display: 'flex',
  flexDirection: 'column',
  // `auto` would let the list's content floor its height and push the popup past `maxHeight`.
  minHeight: 0,
  padding: COMBOBOX_LIST_GUTTER,
  overflowX: 'hidden',
  overflowY: 'auto',
  // Keeps the arrow keys from parking the highlighted option flush against the scrollport, where the
  // fade would eat its top or bottom few pixels.
  scrollPaddingBlock: fadeMaskSizeCSS(COMBOBOX_LIST_GUTTER),
  outline: 'none',

  '&[data-empty]': {
    padding: 0,
  },
});
ComboBoxList.displayName = 'ComboBoxList';

/**
 * The slot between the search field and the options: `actions`, or a select-all row. Collapses when
 * its contents render nothing, which a select-all row does whenever the filter matches no options.
 */
export const ComboBoxActions = styled('div', {
  display: 'flex',
  flexShrink: 0,
  alignItems: 'center',
  gap: '$6',
  padding: '$6 $12 $8',
  strokeBottom: colors.strokeNeutralLight,

  '&:empty': {
    display: 'none',
  },

  [darkThemeSelector]: {
    strokeBottom: colors.strokeNeutralDark,
  },
});
ComboBoxActions.displayName = 'ComboBoxActions';

// A `label` rather than a `div` so a click anywhere in the row — the padding, the icon — lands in
// the nested input, which the browser does for free.
const ComboBoxSearchRow = styled('label', {
  display: 'flex',
  flexShrink: 0,
  alignItems: 'center',
  gap: '$6',
  cursor: 'text',
  padding: '$8 $12',
  strokeBottom: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    strokeBottom: colors.strokeNeutralDark,
  },

  [`&:has(+ ${ComboBoxActions}:not(:empty))`]: {
    strokeBottom: colors.transparent,
  },
});
ComboBoxSearchRow.displayName = 'ComboBoxSearchRow';

const ComboBoxSearchIcon = styled(Icon, {
  flexShrink: 0,
  size: '$12',
  color: colors.iconNeutralLight,

  [darkThemeSelector]: {
    color: colors.iconNeutralDark,
  },
});
ComboBoxSearchIcon.displayName = 'ComboBoxSearchIcon';

const ComboBoxSearchInput = styled(Combobox.Input, {
  flex: 1,
  minWidth: 0,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  ...SmallSansSizes,
  color: colors.headingNeutralLight,

  '&::placeholder': {
    color: colors.controlContentPlaceholderLight,

    [darkThemeSelector]: {
      color: colors.controlContentPlaceholderDark,
    },
  },

  [darkThemeSelector]: {
    color: colors.headingNeutralDark,
  },
});
ComboBoxSearchInput.displayName = 'ComboBoxSearchInput';

export interface ComboBoxSearchProps {
  icon?: IconName;
  placeholder?: string;
}

/**
 * The filter field. Being inside the positioner is what puts Base UI in its input-inside-popup mode:
 * the trigger keeps `role="combobox"` and its closed-state type-ahead, and the field takes focus on
 * open without the roving-tabindex fight a `Select` puts up.
 */
export function ComboBoxSearch({ icon = 'search', placeholder = 'Search' }: ComboBoxSearchProps) {
  return (
    <ComboBoxSearchRow>
      <ComboBoxSearchIcon icon={icon} />
      <ComboBoxSearchInput placeholder={placeholder} />
    </ComboBoxSearchRow>
  );
}

// Base UI keeps the element mounted for its live region and only fills it in once the list is
// empty, so the padding has to go with the content.
export const ComboBoxEmpty = styled(Combobox.Empty, {
  ...SmallSansSizes,
  display: 'flex',
  justifyContent: 'center',
  padding: '$4 $8',
  color: colors.controlContentPlaceholderLight,

  '&:empty': {
    display: 'none',
  },

  [darkThemeSelector]: {
    color: colors.controlContentPlaceholderDark,
  },
});
ComboBoxEmpty.displayName = 'ComboBoxEmpty';

export const ComboBoxGroup = styled(Combobox.Group, {
  display: 'flex',
  flexDirection: 'column',
  gap: '$2',

  '& + &': {
    marginTop: '$4',
  },
  variants: {
    separated: {
      true: {
        '& + &': {
          marginTop: '$8',
          paddingTop: '$4',
          strokeTop: colors.strokeNeutralLight,
          [darkThemeSelector]: { strokeTop: colors.strokeNeutralDark },
        },
      },
    },
  },
});
ComboBoxGroup.displayName = 'ComboBoxGroup';

export const ComboBoxGroupLabel = styled(Combobox.GroupLabel, {
  ...SmallSansSizes,
  padding: '$4 $8 0',
  fontWeight: '$medium',
  color: colors.controlContentPlaceholderLight,

  [darkThemeSelector]: {
    color: colors.controlContentPlaceholderDark,
  },
});
ComboBoxGroupLabel.displayName = 'ComboBoxGroupLabel';

/**
 * The bare option box. Selection and highlight colours are the component's own, so each styles this
 * further rather than inheriting one look.
 */
export const ComboBoxOption = styled(Combobox.Item, {
  position: 'relative',
  display: 'flex',
  padding: '$4 $8',
  borderRadius: '$6',
  outline: 'none',
  cursor: 'pointer',
  userSelect: 'none',

  '&[data-disabled]': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
});
ComboBoxOption.displayName = 'ComboBoxOption';

const ComboBoxListOptionStart = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '$20',

  [devices.touch]: {
    heightAll: '$24',
  },

  [devices.mouse]: {
    heightAll: '$20',
  },
});
ComboBoxListOptionStart.displayName = 'ComboBoxListOptionStart';

const ComboBoxListOptionCheck = styled(Icon, {
  size: '$16',
  // Reserves the column so a label doesn't shift sideways as the selection moves.
  visibility: 'hidden',
});
ComboBoxListOptionCheck.displayName = 'ComboBoxListOptionCheck';

const ComboBoxListOptionLabel = styled(Body, {
  width: '100%',
  color: colors.bodyNeutralLight,
  wordBreak: 'break-word',

  [darkThemeSelector]: {
    color: colors.bodyNeutralDark,
  },
});
ComboBoxListOptionLabel.displayName = 'ComboBoxListOptionLabel';

/**
 * The option body `ComboBox` and `MultiComboBox` share: a checkmark column, then the label. Hover
 * tints the row and `data-highlighted` — the keyboard cursor, which never moves DOM focus — draws
 * the focus ring, matching what `Menu` does for real focus.
 */
export const ComboBoxListOptionRoot = styled(ComboBoxOption, {
  gap: '$6',
  borderRadius: '$8',
  transition: transitions.focusRing,

  [`${ComboBoxListOptionCheck}`]: {
    color: colors.iconNeutralLight,

    [darkThemeSelector]: {
      color: colors.iconNeutralDark,
    },
  },

  '&:hover': {
    zIndex: 2,
    background: colors.bgNeutralLight,
    strokeAll: colors.strokeNeutralLight,

    [`${ComboBoxListOptionLabel}`]: {
      color: colors.headingNeutralLight,
    },

    [darkThemeSelector]: {
      background: colors.bgNeutralDark,
      strokeAll: colors.strokeNeutralDark,

      [`${ComboBoxListOptionLabel}`]: {
        color: colors.headingNeutralDark,
      },
    },
  },

  '&[data-highlighted]': {
    zIndex: 4,
    boxShadow: shadows.focusRingLight,

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },

  '&[data-selected]': {
    zIndex: 3,
    background: colors.bgBrandLight,
    strokeAll: colors.strokeBrandLight,

    [`${ComboBoxListOptionCheck}`]: {
      visibility: 'visible',
      color: colors.bodyBrandLight,
    },

    [`${ComboBoxListOptionLabel}`]: {
      color: colors.headingBrandLight,
    },

    [darkThemeSelector]: {
      background: colors.bgBrandDark,
      strokeAll: colors.strokeBrandDark,

      [`${ComboBoxListOptionCheck}`]: {
        color: colors.bodyBrandDark,
      },

      [`${ComboBoxListOptionLabel}`]: {
        color: colors.headingBrandDark,
      },
    },
  },

  '&[data-disabled]': {
    zIndex: 0,

    '&:hover': {
      background: colors.bgApplicationLight,
      strokeAll: colors.transparent,

      [darkThemeSelector]: {
        background: colors.bgApplicationDark,
      },
    },

    [`${ComboBoxListOptionLabel}`]: {
      color: colors.disabledNeutralLight,

      [darkThemeSelector]: {
        color: colors.disabledNeutralDark,
      },
    },
  },
});
ComboBoxListOptionRoot.displayName = 'ComboBoxListOptionRoot';

export interface ComboBoxListOptionProps {
  row: ComboBoxRow;
  /** Overrides the row's own flag, for a component that also takes a list of disabled values. */
  disabled?: boolean;
  children?: React.ReactNode;
}

/** The inside of a `ComboBoxListOptionRoot`, for a list that renders its own item element. */
export function ComboBoxListOptionBody({ children }: { children: React.ReactNode }) {
  return (
    <>
      <ComboBoxListOptionStart>
        <ComboBoxListOptionCheck icon="checkmark" />
      </ComboBoxListOptionStart>
      <ComboBoxListOptionLabel>{children}</ComboBoxListOptionLabel>
    </>
  );
}

function ComboBoxListOptionImpl({ row, disabled, children }: ComboBoxListOptionProps) {
  return (
    <ComboBoxListOptionRoot value={row.value} disabled={disabled ?? row.disabled}>
      <ComboBoxListOptionBody>{children ?? row.label}</ComboBoxListOptionBody>
    </ComboBoxListOptionRoot>
  );
}

export const ComboBoxListOption = React.memo(ComboBoxListOptionImpl);
